import { Connection, PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import { getCurrentRpcEndpoint, SOLANA_CONFIG } from '../config/security';
import { sanitizeInput, sanitizeErrorMessage, secureStorage } from '../utils/security';
import DAOGovernanceService from './DAOGovernanceService';
import { ReputationService } from './ReputationService';
import ProofOfHumanityService from './ProofOfHumanityService';
import DynamicGovernanceConfig from './DynamicGovernanceConfig';

/**
 * Admin Panel Service for Human Oversight
 * Manages content moderation, user sanctions, and platform governance
 */
export class AdminPanelService {
  constructor(connection, wallet) {
    this.connection = connection || new Connection(getCurrentRpcEndpoint());
    this.wallet = wallet;
    this.daoService = new DAOGovernanceService(connection, wallet);
    this.reputationService = new ReputationService(connection, wallet);
    this.humanityService = new ProofOfHumanityService(connection, wallet);
    this.znoozinuTokenAddress = new PublicKey('5cLKdANQXzUPMXgpba19Zwprhmi8yQW6R5D66oTQZ9ZN');
    
    // Admin role requirements (based on ZNOOZINU token holdings)
    this.ADMIN_ROLES = {
      MODERATOR: {
        minTokens: 50000,
        permissions: ['review_flags', 'moderate_content', 'issue_warnings'],
        description: 'Content moderation and user warnings'
      },
      ADMIN: {
        minTokens: 100000,
        permissions: ['all_moderator', 'user_sanctions', 'policy_changes', 'system_config'],
        description: 'Full administrative access'
      },
      SUPER_ADMIN: {
        minTokens: 250000,
        permissions: ['all_admin', 'role_management', 'treasury_access', 'emergency_actions'],
        description: 'Highest level administrative access'
      }
    };

    // Action types for admin logging
    this.ADMIN_ACTIONS = {
      CONTENT_APPROVED: 'Content approved after review',
      CONTENT_REMOVED: 'Content removed by admin',
      USER_WARNED: 'User issued warning',
      USER_SUSPENDED: 'User account suspended',
      USER_BANNED: 'User account banned',
      REPUTATION_ADJUSTED: 'User reputation manually adjusted',
      VERIFICATION_APPROVED: 'Humanity verification approved',
      VERIFICATION_REJECTED: 'Humanity verification rejected',
      POLICY_UPDATED: 'Platform policy updated',
      EMERGENCY_ACTION: 'Emergency action taken'
    };

    // Content moderation queue priorities
    this.PRIORITY_LEVELS = {
      CRITICAL: { level: 1, color: '#dc3545', description: 'Immediate attention required' },
      HIGH: { level: 2, color: '#fd7e14', description: 'Review within 2 hours' },
      MEDIUM: { level: 3, color: '#ffc107', description: 'Review within 24 hours' },
      LOW: { level: 4, color: '#28a745', description: 'Review when convenient' }
    };
  }

  /**
 * Check if user has admin permissions
 * UPDATED: Now integrates with multi-signature governance system
 */
async checkAdminPermissions(walletAddress, requiredRole = 'MODERATOR') {
  try {
    // PRIORITY CHECK: Multi-signature governance admin wallets have full access
    const isMultiSigAdmin = DynamicGovernanceConfig.isAdmin(walletAddress);
    if (isMultiSigAdmin) {
      console.log(`🔒 Multi-sig admin access granted for ${walletAddress}`);
      return {
        hasPermission: true,
        role: 'MULTI_SIG_ADMIN',
        isMultiSigAdmin: true,
        tokenBalance: 'N/A - Multi-sig admin',
        reputation: 'N/A - Multi-sig admin',
        permissions: ['FULL_ADMIN_ACCESS', 'BLOCK_USERS', 'BLOCK_IPS', 'MODERATE_CONTENT', 'SYSTEM_MANAGEMENT']
      };
    }

    // Standard token-based admin role check for non-multi-sig admins
    const tokenBalance = await this.getTokenBalance(walletAddress);
    const requiredTokens = this.ADMIN_ROLES[requiredRole]?.minTokens || 0;

    if (tokenBalance < requiredTokens) {
      return {
        hasPermission: false,
        reason: 'INSUFFICIENT_TOKENS',
        required: requiredTokens,
        current: tokenBalance
      };
    }

    // Check if user is in good standing (not banned/suspended)
    const userStatus = await this.getUserStatus(walletAddress);
    if (userStatus.status === 'BANNED' || userStatus.status === 'SUSPENDED') {
      return {
        hasPermission: false,
        reason: 'ACCOUNT_RESTRICTED',
        status: userStatus.status
      };
    }

    // Get user's reputation
    const reputation = await this.reputationService.getUserReputation(walletAddress);
    if (reputation.score < 500) { // Minimum reputation for admin roles
      return {
        hasPermission: false,
        reason: 'INSUFFICIENT_REPUTATION',
        required: 500,
        current: reputation.score
      };
    }

    return {
      hasPermission: true,
      role: requiredRole,
      isMultiSigAdmin: false,
      tokenBalance,
      reputation: reputation.score,
      permissions: this.ADMIN_ROLES[requiredRole].permissions
    };
  } catch (error) {
    console.error('Error checking admin permissions:', sanitizeErrorMessage(error));
    return {
      hasPermission: false,
      reason: 'ERROR',
      error: sanitizeErrorMessage(error)
    };
  }
}

  /**
   * Get moderation queue with flagged content
   */
  async getModerationQueue(filters = {}) {
    try {
      const queue = [];
      
      // Get escalated DAO proposals
      const escalatedProposals = this.getEscalatedProposals();
      
      // Get direct admin review queue
      const adminQueue = secureStorage.getItem('admin_review_queue') || [];
      
      // Get flagged content from blockchain
      const flaggedContent = await this.getFlaggedContentFromBlockchain();
      
      // Combine all sources
      const allItems = [
        ...escalatedProposals.map(item => ({ ...item, source: 'DAO_ESCALATION' })),
        ...adminQueue.map(item => ({ ...item, source: 'ADMIN_QUEUE' })),
        ...flaggedContent.map(item => ({ ...item, source: 'BLOCKCHAIN_FLAG' }))
      ];

      // Apply filters
      let filteredItems = allItems;
      
      if (filters.priority) {
        filteredItems = filteredItems.filter(item => item.priority === filters.priority);
      }
      
      if (filters.category) {
        filteredItems = filteredItems.filter(item => 
          item.flagData?.category === filters.category
        );
      }
      
      if (filters.status) {
        filteredItems = filteredItems.filter(item => item.status === filters.status);
      }

      // Sort by priority and timestamp
      filteredItems.sort((a, b) => {
        const priorityA = this.PRIORITY_LEVELS[a.priority]?.level || 999;
        const priorityB = this.PRIORITY_LEVELS[b.priority]?.level || 999;
        
        if (priorityA !== priorityB) {
          return priorityA - priorityB; // Higher priority first
        }
        
        return (b.timestamp || b.createdAt || 0) - (a.timestamp || a.createdAt || 0);
      });

      return {
        success: true,
        queue: filteredItems,
        total: filteredItems.length,
        summary: this.getQueueSummary(filteredItems)
      };
    } catch (error) {
      console.error('Error getting moderation queue:', sanitizeErrorMessage(error));
      return {
        success: false,
        error: sanitizeErrorMessage(error)
      };
    }
  }

  /**
   * Get escalated DAO proposals
   */
  getEscalatedProposals() {
    try {
      const proposals = this.daoService.getActiveProposals();
      return proposals.filter(proposal => 
        proposal.results?.winningOption === 'ESCALATE' ||
        proposal.flagData?.severity === 'HIGH'
      ).map(proposal => ({
        id: proposal.id,
        type: 'DAO_PROPOSAL',
        contentId: proposal.contentId,
        flagData: proposal.flagData,
        priority: this.determinePriority(proposal.flagData),
        status: 'PENDING_ADMIN_REVIEW',
        createdAt: proposal.createdAt,
        daoResults: proposal.results
      }));
    } catch (error) {
      console.error('Error getting escalated proposals:', sanitizeErrorMessage(error));
      return [];
    }
  }

  /**
   * Get flagged content from blockchain
   */
  async getFlaggedContentFromBlockchain() {
    try {
      // This would scan recent transactions for flag events
      // For now, return empty array (would need more complex implementation)
      return [];
    } catch (error) {
      console.error('Error getting flagged content:', sanitizeErrorMessage(error));
      return [];
    }
  }

  /**
   * Determine priority level for flagged content
   */
  determinePriority(flagData) {
    if (!flagData) return 'LOW';
    
    switch (flagData.severity) {
      case 'HIGH':
        return 'CRITICAL';
      case 'MEDIUM':
        return flagData.category === 'HARASSMENT' ? 'HIGH' : 'MEDIUM';
      case 'LOW':
        return 'LOW';
      default:
        return 'MEDIUM';
    }
  }

  /**
   * Get queue summary statistics
   */
  getQueueSummary(items) {
    const summary = {
      total: items.length,
      byPriority: {},
      byCategory: {},
      byStatus: {},
      avgAge: 0
    };

    let totalAge = 0;
    
    for (const item of items) {
      // Count by priority
      const priority = item.priority || 'LOW';
      summary.byPriority[priority] = (summary.byPriority[priority] || 0) + 1;
      
      // Count by category
      const category = item.flagData?.category || 'OTHER';
      summary.byCategory[category] = (summary.byCategory[category] || 0) + 1;
      
      // Count by status
      const status = item.status || 'UNKNOWN';
      summary.byStatus[status] = (summary.byStatus[status] || 0) + 1;
      
      // Calculate age
      const age = Date.now() - (item.timestamp || item.createdAt || Date.now());
      totalAge += age;
    }

    if (items.length > 0) {
      summary.avgAge = Math.floor(totalAge / items.length / (1000 * 60 * 60)); // Hours
    }

    return summary;
  }

  /**
   * Review and moderate content
   */
  async moderateContent(itemId, decision, reason, adminWalletAddress) {
    try {
      // Check admin permissions
      const permissions = await this.checkAdminPermissions(adminWalletAddress, 'MODERATOR');
      if (!permissions.hasPermission) {
        throw new Error(`Access denied: ${permissions.reason}`);
      }

      // Get the item to moderate
      const item = await this.getModerationItem(itemId);
      if (!item) {
        throw new Error('Moderation item not found');
      }

      // Sanitize inputs
      const sanitizedReason = sanitizeInput(reason);

      // Create moderation decision
      const moderationDecision = {
        itemId,
        contentId: item.contentId,
        decision, // 'APPROVE', 'REMOVE', 'WARN', 'ESCALATE'
        reason: sanitizedReason,
        moderator: adminWalletAddress,
        timestamp: Date.now(),
        originalFlag: item.flagData
      };

      // Execute the decision
      await this.executeModerationDecision(moderationDecision);

      // Log admin action
      await this.logAdminAction({
        action: this.getActionTypeFromDecision(decision),
        details: moderationDecision,
        admin: adminWalletAddress,
        timestamp: Date.now()
      });

      // Update item status
      await this.updateModerationItemStatus(itemId, 'RESOLVED', moderationDecision);

      return {
        success: true,
        decision: moderationDecision,
        message: `Content ${decision.toLowerCase()} successfully`
      };
    } catch (error) {
      console.error('Error moderating content:', sanitizeErrorMessage(error));
      return {
        success: false,
        error: sanitizeErrorMessage(error)
      };
    }
  }

  /**
   * Get moderation item by ID
   */
  async getModerationItem(itemId) {
    try {
      // Check admin queue
      const adminQueue = secureStorage.getItem('admin_review_queue') || [];
      const adminItem = adminQueue.find(item => item.id === itemId);
      if (adminItem) return adminItem;

      // Check DAO proposals
      const proposal = this.daoService.getProposal(itemId);
      if (proposal) return proposal;

      return null;
    } catch (error) {
      console.error('Error getting moderation item:', sanitizeErrorMessage(error));
      return null;
    }
  }

  /**
   * Execute moderation decision
   */
  async executeModerationDecision(decision) {
    try {
      switch (decision.decision) {
        case 'APPROVE':
          await this.approveContent(decision.contentId);
          break;
        case 'REMOVE':
          await this.removeContent(decision.contentId);
          break;
        case 'WARN':
          await this.warnUser(decision.originalFlag.flagger, decision.reason);
          break;
        case 'ESCALATE':
          await this.escalateToSuperAdmin(decision);
          break;
      }

      // Create blockchain record of admin decision
      if (this.wallet) {
        await this.recordAdminDecisionOnChain(decision);
      }
    } catch (error) {
      console.error('Error executing moderation decision:', sanitizeErrorMessage(error));
      throw error;
    }
  }

  /**
   * Approve content (remove flags)
   */
  async approveContent(contentId) {
    secureStorage.setItem(`content_status_${contentId}`, {
      status: 'APPROVED',
      reason: 'ADMIN_REVIEW',
      timestamp: Date.now()
    });
  }

  /**
   * Remove content
   */
  async removeContent(contentId) {
    secureStorage.setItem(`content_status_${contentId}`, {
      status: 'REMOVED',
      reason: 'ADMIN_DECISION',
      timestamp: Date.now()
    });
  }

  /**
   * Warn user
   */
  async warnUser(userAddress, reason) {
    const warnings = secureStorage.getItem(`user_warnings_${userAddress}`) || [];
    warnings.push({
      reason,
      timestamp: Date.now(),
      type: 'CONTENT_WARNING'
    });
    secureStorage.setItem(`user_warnings_${userAddress}`, warnings);
  }

  /**
   * Escalate to super admin
   */
  async escalateToSuperAdmin(decision) {
    const superAdminQueue = secureStorage.getItem('super_admin_queue') || [];
    superAdminQueue.push({
      ...decision,
      escalatedAt: Date.now(),
      status: 'PENDING_SUPER_ADMIN'
    });
    secureStorage.setItem('super_admin_queue', superAdminQueue);
  }

  /**
   * Record admin decision on blockchain
   */
  async recordAdminDecisionOnChain(decision) {
    try {
      const transaction = new Transaction();
      
      const adminInstruction = new TransactionInstruction({
        keys: [
          { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true }
        ],
        programId: new PublicKey(SOLANA_CONFIG.PROGRAM_IDS.CHIRPX),
        data: Buffer.from(`ChirpX Admin Decision: ${JSON.stringify({
          itemId: decision.itemId,
          contentId: decision.contentId,
          decision: decision.decision,
          moderator: decision.moderator,
          timestamp: decision.timestamp
        })}`, 'utf8')
      });

      transaction.add(adminInstruction);

      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;

      const signedTransaction = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      
      await this.connection.confirmTransaction(signature, 'confirmed');

      return signature;
    } catch (error) {
      console.error('Error recording admin decision:', sanitizeErrorMessage(error));
      throw error;
    }
  }

  /**
   * Get action type from decision
   */
  getActionTypeFromDecision(decision) {
    switch (decision) {
      case 'APPROVE':
        return 'CONTENT_APPROVED';
      case 'REMOVE':
        return 'CONTENT_REMOVED';
      case 'WARN':
        return 'USER_WARNED';
      default:
        return 'CONTENT_REVIEWED';
    }
  }

  /**
   * Log admin action
   */
  async logAdminAction(actionData) {
    try {
      const adminLogs = secureStorage.getItem('admin_action_logs') || [];
      adminLogs.unshift(actionData); // Add to beginning
      
      // Keep only last 1000 actions
      if (adminLogs.length > 1000) {
        adminLogs.splice(1000);
      }
      
      secureStorage.setItem('admin_action_logs', adminLogs);
    } catch (error) {
      console.error('Error logging admin action:', sanitizeErrorMessage(error));
    }
  }

  /**
   * Update moderation item status
   */
  async updateModerationItemStatus(itemId, status, decision) {
    try {
      // Update in admin queue
      const adminQueue = secureStorage.getItem('admin_review_queue') || [];
      const itemIndex = adminQueue.findIndex(item => item.id === itemId);
      
      if (itemIndex !== -1) {
        adminQueue[itemIndex].status = status;
        adminQueue[itemIndex].decision = decision;
        adminQueue[itemIndex].resolvedAt = Date.now();
        secureStorage.setItem('admin_review_queue', adminQueue);
      }
    } catch (error) {
      console.error('Error updating item status:', sanitizeErrorMessage(error));
    }
  }

  /**
   * Get user status (banned, suspended, etc.)
   */
  async getUserStatus(walletAddress) {
    try {
      const userStatus = secureStorage.getItem(`user_status_${walletAddress}`) || {
        status: 'ACTIVE',
        lastUpdated: Date.now()
      };
      
      return userStatus;
    } catch (error) {
      console.error('Error getting user status:', sanitizeErrorMessage(error));
      return { status: 'ACTIVE', error: true };
    }
  }

  /**
   * Get token balance for role verification
   */
  async getTokenBalance(walletAddress) {
    try {
      const tokenAccounts = await this.connection.getTokenAccountsByOwner(
        new PublicKey(walletAddress),
        { mint: this.znoozinuTokenAddress }
      );

      let totalBalance = 0;
      for (const account of tokenAccounts.value) {
        const accountInfo = await this.connection.getTokenAccountBalance(account.pubkey);
        totalBalance += parseInt(accountInfo.value.amount) / Math.pow(10, accountInfo.value.decimals);
      }

      return totalBalance;
    } catch (error) {
      console.error('Error getting token balance:', sanitizeErrorMessage(error));
      return 0;
    }
  }

  /**
   * Get admin dashboard data
   */
  async getAdminDashboard(adminWalletAddress) {
    try {
      const permissions = await this.checkAdminPermissions(adminWalletAddress);
      if (!permissions.hasPermission) {
        throw new Error('Access denied');
      }

      const moderationQueue = await this.getModerationQueue();
      const adminLogs = secureStorage.getItem('admin_action_logs') || [];
      const recentLogs = adminLogs.slice(0, 10);

      return {
        success: true,
        permissions,
        moderationQueue: moderationQueue.queue,
        queueSummary: moderationQueue.summary,
        recentActions: recentLogs,
        systemStats: await this.getSystemStats()
      };
    } catch (error) {
      console.error('Error getting admin dashboard:', sanitizeErrorMessage(error));
      return {
        success: false,
        error: sanitizeErrorMessage(error)
      };
    }
  }

  /**
   * Get system statistics
   */
  async getSystemStats() {
    try {
      // This would typically query the database/blockchain for stats
      return {
        totalUsers: 0, // Would be calculated from blockchain data
        totalPosts: 0,
        totalFlags: 0,
        activeProposals: this.daoService.getActiveProposals().length,
        pendingVerifications: 0
      };
    } catch (error) {
      console.error('Error getting system stats:', sanitizeErrorMessage(error));
      return {};
    }
  }
}

export default AdminPanelService;
